import { Injectable } from '@angular/core';
import moment from 'moment';
import { groupBy } from 'lodash';
import { InsectForecast, InsectForecastAggregated } from './../../_models/insects/insectForecast';
import { ChartDatasetBuilder, Measurement } from './../../_models/insects/measurement';
import { InsectsZoneService } from './insectsZone.service';

export interface LegendItem {
  id: number;
  color: string;
  label: string;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  disabled?: boolean;
}

export enum ChartDataIndex {
  DELTA,
  COUNTS,
  VALIDATIONS,
  GDD,
  GDD_FORECAST,
}
export interface PlotBand {
  from: number;
  to: number;
  type: string;
  color: string;
}

@Injectable()
export class InsectsChartsService {
  constructor(private insectZoneService: InsectsZoneService) {}

  buildChartDataset(
    measurements: Measurement[] = [],
    forecasts: (InsectForecast | InsectForecastAggregated)[] = [],
    hasImageRecognition: boolean = true
  ): ChartDatasetBuilder {
    const dataset = [[], [], [], [], []];
    measurements.forEach((measure) => {
      const timestamp = moment(measure.dateOfCapture).valueOf();
      if (hasImageRecognition) {
        dataset[ChartDataIndex.DELTA].push([timestamp, this.removeNegative(measure.delta)]);
        dataset[ChartDataIndex.COUNTS].push([timestamp, this.removeNegative(measure.counts)]);
        dataset[ChartDataIndex.VALIDATIONS].push([
          timestamp,
          this.removeNegative(measure.validations),
        ]);
      } else {
        dataset[ChartDataIndex.DELTA].push([timestamp, this.removeNegative(measure.delta)]);
        dataset[ChartDataIndex.COUNTS].push([timestamp, this.removeNegative(measure.inCount)]);
        dataset[ChartDataIndex.VALIDATIONS].push([
          timestamp,
          this.removeNegative(measure.validations),
        ]);
      }
    });

    const today = moment();
    const phasesMap = new Map<string, number>();
    forecasts.forEach((forecast: any) => {
      const timestamp = moment(forecast.date).valueOf();

      let value = null;
      if (forecast.gddAvg !== undefined) {
        value = forecast.gddAvg;
      } else {
        value = forecast.gddCumulated;
      }

      if (today.isSame(forecast.date, 'day')) {
        dataset[ChartDataIndex.GDD].push([timestamp, value]);
        dataset[ChartDataIndex.GDD_FORECAST].push([timestamp, value]);
      } else if (today.isAfter(timestamp)) {
        dataset[ChartDataIndex.GDD].push([timestamp, value]);
      } else {
        dataset[ChartDataIndex.GDD_FORECAST].push([timestamp, value]);
      }
      const mapKey: string = forecast.insectPhase;
      if (phasesMap.has(mapKey)) {
        const value = phasesMap.get(mapKey);
        phasesMap.set(mapKey, value + 1);
      } else {
        phasesMap.set(mapKey, 1);
      }
    });

    return {
      dataset,
      phasesMap,
    };
  }

  private removeNegative(value: number) {
    return !value || value < 0 ? 0 : value;
  }

  getPlotBandsFromForecasts(forecasts: InsectForecast[]): PlotBand[] {
    const ranges: PlotBand[] = [];
    let previousPlotBand: PlotBand = null;
    forecasts.forEach((m, i) => {
      const date = moment(m.date.valueOf()).toDate();
      if (!i) {
        previousPlotBand = {
          from: 0,
          to: null,
          type: m.insectPhase,
          color: this.insectZoneService.getInsectPhaseColorByPhaseCode(m.insectPhase),
        };
      }
      if (i === forecasts.length - 1) {
        previousPlotBand.to = moment(date).valueOf() * 2;
        ranges.push(previousPlotBand);
      }
      if (
        previousPlotBand &&
        m.insectPhase !== previousPlotBand.type &&
        i !== forecasts.length - 1
      ) {
        previousPlotBand.to = moment(date).valueOf();
        ranges.push(previousPlotBand);
        previousPlotBand = {
          from: moment(date).valueOf(),
          to: null,
          type: m.insectPhase,
          color: this.insectZoneService.getInsectPhaseColorByPhaseCode(m.insectPhase),
        };
      }
    });
    return ranges;
  }

  getLegendItemsFromForecasts(
    forecasts: InsectForecast[],
    addGddItem: boolean = false
  ): LegendItem[] {
    const gddItem: LegendItem = {
      id: 0,
      label: 'insects.gdd',
      color: '#7bb4ec',
    };

    const groupedMeasurements = groupBy(forecasts, 'insectPhase');
    const phases = Object.keys(groupedMeasurements).map((phase, index) => {
      return {
        id: index + 1,
        color: this.insectZoneService.getInsectPhaseColorByPhaseCode(phase),
        label: 'insects.' + phase,
      } as LegendItem;
    });

    return addGddItem ? [gddItem].concat(phases) : phases;
  }
}
